const VERTEX_SHADER = 'Vertex shader';
const FRAGMENT_SHADER = 'Fragment shader';
const SHADER_PROGRAM = 'Shader program';
const VERTEX_BUFFER = 'Vertex buffer';
const INDEX_BUFFER = 'Index buffer';
const TEXTURE = 'Texture';

import Cache from '../core/Cache';
import GraphicObject from './GraphicObject';
import VertexShader from './VertexShader';
import FragmentShader from './FragmentShader';
import ShaderProgram from './ShaderProgram';
import VertexBuffer from './VertexBuffer';
import IndexBuffer from './IndexBuffer';
import Texture from './Texture';

export default class GraphicObjectRepository {

	static _makeKey(name, type) {
		return `${type} "${name}"`;
	}

	_gl = null;
	_cache = null;

	_add(name, type, value) {
		this._cache.put(GraphicObjectRepository._makeKey(name, type), value);
		return value;
	}

	_get(name, type) {
		return this._cache.get(GraphicObjectRepository._makeKey(name, type));
	}

	_release(name, type) {
		const key = GraphicObjectRepository._makeKey(name, type);
		const obj = this._cache.get(key);
		obj.release();
		this._cache.remove(key);
	}

	constructor(gl) {

		this._gl = gl;

		this._cache = new Cache(null, (key, value) => {
			if (!(value instanceof GraphicObject)) {
				throw new Error('Invalid graphic object');
			}
		});

	}

	createVertexShader(name, src) {
		return this._add(name, VERTEX_SHADER, new VertexShader(this._gl, src));
	}

	createFragmentShader(name, src) {
		return this._add(name, FRAGMENT_SHADER, new FragmentShader(this._gl, src));
	}

	createShaderProgram(name, vertexShader, fragmentShader) {
		if (!(vertexShader instanceof VertexShader)) {
			vertexShader = this.createVertexShader(name, vertexShader);
			fragmentShader = this.createFragmentShader(name, fragmentShader);
		}
		return this._add(name, SHADER_PROGRAM, new ShaderProgram(this._gl, vertexShader.getObject(), fragmentShader.getObject()));
	}

	createVertexBuffer(name, attributeName, itemSize, dynamic = true) {
		return this._add(name, VERTEX_BUFFER, new VertexBuffer(this._gl, attributeName, itemSize, dynamic));
	}

	createIndexBuffer(name, attributeName, itemSize, dynamic = true) {
		return this._add(name, INDEX_BUFFER, new IndexBuffer(this._gl, attributeName, itemSize, dynamic));
	}

	createTexture(name) {
		return this._add(name, TEXTURE, new Texture(this._gl));
	}


	getVertexShader(name) {
		return this._get(name, VERTEX_SHADER);
	}

	getFragmentShader(name) {
		return this._get(name, FRAGMENT_SHADER);
	}

	getShaderProgram(name) {
		return this._get(name, SHADER_PROGRAM);
	}

	getVertexBuffer(name) {
		return this._get(name, VERTEX_BUFFER);
	}

	getIndexBuffer(name) {
		return this._get(name, INDEX_BUFFER);
	}

	getTexture(name) {
		return this._get(name, TEXTURE);
	}


	releaseVertexShader(name) {
		return this._release(name, VERTEX_SHADER);
	}

	releaseFragmentShader(name) {
		return this._release(name, FRAGMENT_SHADER);
	}

	releaseShaderProgram(name) {
		return this._release(name, SHADER_PROGRAM);
	}

	releaseVertexBuffer(name) {
		return this._release(name, VERTEX_BUFFER);
	}

	releaseIndexBuffer(name) {
		return this._release(name, INDEX_BUFFER);
	}

	releaseTexture(name) {
		return this._release(name, TEXTURE);
	}

}
